import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info } from 'lucide-react';
import Button from './button/Button';
import ModalShell from './modal/ModalShell';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'danger' | 'warning' | 'info';
  /** `danger` renders the confirm as the solid red destructive button. */
  confirmVariant?: 'primary' | 'danger';
  /** Keeps confirm disabled with a spinner, e.g. until the text it confirms has loaded. */
  confirmPending?: boolean;
}

/** Confirm/cancel dialog on the shared ModalShell (focus trap included). */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  variant = 'warning',
  confirmVariant = 'primary',
  confirmPending = false
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const icon =
    variant === 'danger' ? (
      <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
    ) : variant === 'info' ? (
      <Info className="h-6 w-6 text-primary-40 dark:text-primary-90" aria-hidden="true" />
    ) : (
      <AlertTriangle className="h-6 w-6 text-secondary-40 dark:text-secondary-70" aria-hidden="true" />
    );

  return (
    <ModalShell
      onClose={onCancel}
      labelledBy="modal-title"
      describedBy="modal-description"
    >
      <div className="p-6">
        {/* Icon and Title */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-shrink-0">{icon}</div>
          <div className="flex-1">
            <h3
              id="modal-title"
              className="text-lg font-semibold text-grey-10 dark:text-grey-90"
            >
              {title}
            </h3>
          </div>
        </div>

        {/* Message */}
        <div className="mb-6">
          <p id="modal-description" className="text-grey-30 dark:text-grey-70">
            {message}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {cancelText}
          </Button>
          <Button type="button" variant={confirmVariant} onClick={onConfirm} disabled={confirmPending}>
            <span className="inline-flex items-center gap-2">
              {confirmPending && (
                <span
                  role="status"
                  aria-label={t('common.loading', 'Loading...')}
                  className="animate-spin rounded-full h-4 w-4 border-2 border-primary-40 dark:border-primary-90 border-t-transparent"
                />
              )}
              {confirmText}
            </span>
          </Button>
        </div>
      </div>
    </ModalShell>
  );
};

export default ConfirmationModal;
